/**
 * Admin money display helpers — dual gateway rules:
 *   Stripe → always USD (native cents)
 *   MercadoPago → always ARS (native cents)
 *
 * Do not sum mixed currencies into a single unlabeled total without
 * converting and labeling the conversion explicitly.
 */

export type AdminPaymentGateway = "stripe" | "mercadopago";

const USD = new Intl.NumberFormat("es-AR", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const USD_COMPACT = new Intl.NumberFormat("es-AR", {
  style: "currency",
  currency: "USD",
  notation: "compact",
  maximumFractionDigits: 1,
});

const USD_WHOLE = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});

const ARS = new Intl.NumberFormat("es-AR", {
  style: "currency",
  currency: "ARS",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const ARS_COMPACT = new Intl.NumberFormat("es-AR", {
  style: "currency",
  currency: "ARS",
  notation: "compact",
  maximumFractionDigits: 1,
});

function safeCents(cents: number): number {
  return Number.isFinite(cents) ? cents : 0;
}

/** Stripe / USD amounts stored as cents. */
export function formatUsdCents(cents: number, opts?: { whole?: boolean }): string {
  const n = safeCents(cents) / 100;
  if (opts?.whole) {
    if (n <= 0) return "$0";
    return USD_WHOLE.format(n);
  }
  return USD.format(n);
}

/** Compact axis/tooling for USD major units (not cents). */
export function formatUsdMajorCompact(value: number): string {
  return USD_COMPACT.format(Number.isFinite(value) ? value : 0);
}

/** MercadoPago / ARS amounts stored as cents. */
export function formatArsCents(cents: number): string {
  return ARS.format(safeCents(cents) / 100);
}

/** Compact axis for ARS major units (not cents). */
export function formatArsMajorCompact(value: number): string {
  return ARS_COMPACT.format(Number.isFinite(value) ? value : 0);
}

export function formatCount(n: number): string {
  return new Intl.NumberFormat("es-AR").format(Number.isFinite(n) ? n : 0);
}

export function gatewayDisplayName(gateway: AdminPaymentGateway): string {
  return gateway === "stripe" ? "Stripe" : "MercadoPago";
}

export function gatewayCurrencyCode(gateway: AdminPaymentGateway): "USD" | "ARS" {
  return gateway === "stripe" ? "USD" : "ARS";
}

/** Short scannable badge label: "Stripe · USD" / "MercadoPago · ARS". */
export function gatewayMoneyLabel(gateway: AdminPaymentGateway): string {
  return `${gatewayDisplayName(gateway)} · ${gatewayCurrencyCode(gateway)}`;
}

/**
 * Format native cents for a gateway without cross-currency mixing.
 */
export function formatGatewayCents(
  cents: number,
  gateway: AdminPaymentGateway,
  opts?: { whole?: boolean },
): string {
  return gateway === "stripe"
    ? formatUsdCents(cents, opts)
    : formatArsCents(cents);
}

/**
 * Format an arbitrary ISO currency code amount stored as cents
 * (e.g. stripe by_currency breakdown).
 */
export function formatCurrencyCents(cents: number, currency: string): string {
  const amount = safeCents(cents) / 100;
  const code = (currency || "USD").toUpperCase();
  try {
    return new Intl.NumberFormat("es-AR", {
      style: "currency",
      currency: code,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${code} ${amount.toFixed(2)}`;
  }
}
